import { useState } from 'react'
import { motion } from 'framer-motion'
import '../styles/AgendarVisita.css'
import { bff, BffError } from '@/lib/bff'
import { LampContainer } from '@/components/ui/lamp'

const segmentos = [
  'Supermercado', 'Autopeças', 'Material de Construção', 'Distribuidora / Atacadista',
  'Loja de Roupas', 'Agropecuária / Pet Shop', 'Papelaria', 'Restaurante / Lanchonete',
  'Posto de Combustível', 'Engenharia / Construção', 'Distribuidora de Água',
  'E-commerce', 'Ótica', 'Outro',
]

const demonstracao = [
  'Funcionalidades específicas para seu segmento',
  'PDV e emissão de NF-e / NFC-e',
  'Controle de estoque e compras',
  'Financeiro: contas a pagar e receber',
  'Relatórios gerenciais e gráficos',
  'Integração com balanças e equipamentos',
]

interface DadosAgendamento {
  nome:     string
  telefone: string
  empresa:  string
  cidade:   string
  segmento: string
  mensagem: string
}

const FORM_VAZIO: DadosAgendamento = {
  nome: '', telefone: '', empresa: '', cidade: '', segmento: '', mensagem: '',
}

type Envio =
  | { tipo: 'idle' }
  | { tipo: 'enviando' }
  | { tipo: 'sucesso' }
  | { tipo: 'erro'; mensagem: string }

// (00) 00000-0000 enquanto digita; aceita fixo com 10 dígitos
function formatarTelefone(valor: string) {
  const d = valor.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 2) return d.length ? `(${d}` : ''
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

export default function AgendarVisita() {
  const [dados, setDados] = useState<DadosAgendamento>(FORM_VAZIO)
  const [envio, setEnvio] = useState<Envio>({ tipo: 'idle' })

  function atualizar(campo: keyof DadosAgendamento) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const valor = campo === 'telefone' ? formatarTelefone(e.target.value) : e.target.value
      setDados(d => ({ ...d, [campo]: valor }))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const digitos = dados.telefone.replace(/\D/g, '')
    if (digitos.length < 10 || digitos.length > 13) {
      setEnvio({ tipo: 'erro', mensagem: 'Informe um telefone válido com DDD.' })
      return
    }

    setEnvio({ tipo: 'enviando' })
    try {
      await bff.post<{ mensagem: string }>('/v1/agendamentos', dados)
      setEnvio({ tipo: 'sucesso' })
      setDados(FORM_VAZIO)
    } catch (err) {
      if (err instanceof BffError && err.status === 429) {
        setEnvio({ tipo: 'erro', mensagem: 'Você já enviou várias solicitações. Aguarde alguns minutos e tente novamente.' })
      } else if (err instanceof BffError && err.status === 400) {
        setEnvio({ tipo: 'erro', mensagem: 'Verifique os dados preenchidos e tente novamente.' })
      } else {
        setEnvio({ tipo: 'erro', mensagem: 'Não foi possível enviar sua solicitação agora. Tente novamente ou fale conosco pelo WhatsApp (63) 98404-2819.' })
      }
    }
  }

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* ══ HERO ══ */}
      <LampContainer
        className="min-h-[22rem] md:min-h-[25rem] rounded-none pt-36 md:pt-44"
        lampClassName="translate-y-10"
        contentClassName="-translate-y-32"
        overlay={
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26, 62, 115, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(26, 62, 115, 0.15) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        }
      >
        <motion.h1
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-8 bg-gradient-to-br from-slate-300 to-slate-500 py-4 bg-clip-text text-center text-3xl font-medium tracking-tight text-transparent sm:text-4xl md:text-7xl px-4"
        >
          Agende uma Visita
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.45,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-2 max-w-xl text-center text-base text-slate-400 px-4"
        >
          Preencha o formulário e um consultor Quantum entrará em contato para agendar
          uma demonstração gratuita diretamente na sua empresa.
        </motion.p>
      </LampContainer>

      {/* ══ CORPO ══ */}
      <div className="av-body">
        <div className="av-body-inner">

          {/* ── Formulário ── */}
          <div className="av-card">
            {envio.tipo === 'sucesso' ? (
              <div className="av-sucesso" role="status">
                <div className="av-sucesso-icon">
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                </div>
                <h2 className="av-card-title">Solicitação enviada!</h2>
                <p className="av-card-sub">
                  Um consultor Quantum entrará em contato em breve para agendar sua demonstração.
                </p>
                <button type="button" className="av-btn-outline" onClick={() => setEnvio({ tipo: 'idle' })}>
                  Enviar outra solicitação
                </button>
              </div>
            ) : (
              <>
                <div className="av-card-head">
                  <div className="av-card-icon">
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="av-card-title">Solicite sua demonstração</h2>
                    <p className="av-card-sub">Preencha seus dados e retornaremos para combinar o melhor dia.</p>
                  </div>
                </div>

                <form className="av-form" onSubmit={handleSubmit}>
                  <div className="av-grid">
                    <div className="av-field">
                      <label htmlFor="av-nome" className="av-label">Nome *</label>
                      <div className="av-input-wrap">
                        <i className="fa-regular fa-user" aria-hidden="true" />
                        <input
                          id="av-nome" name="nome" type="text" required maxLength={120}
                          autoComplete="name" placeholder="Seu nome completo"
                          value={dados.nome} onChange={atualizar('nome')} className="av-input"
                        />
                      </div>
                    </div>
                    <div className="av-field">
                      <label htmlFor="av-telefone" className="av-label">Telefone / WhatsApp *</label>
                      <div className="av-input-wrap">
                        <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                        <input
                          id="av-telefone" name="telefone" type="tel" inputMode="tel" required maxLength={20}
                          autoComplete="tel" placeholder="(00) 00000-0000"
                          value={dados.telefone} onChange={atualizar('telefone')} className="av-input"
                        />
                      </div>
                    </div>
                    <div className="av-field">
                      <label htmlFor="av-empresa" className="av-label">Empresa *</label>
                      <div className="av-input-wrap">
                        <i className="fa-regular fa-building" aria-hidden="true" />
                        <input
                          id="av-empresa" name="empresa" type="text" required maxLength={150}
                          autoComplete="organization" placeholder="Nome da sua empresa"
                          value={dados.empresa} onChange={atualizar('empresa')} className="av-input"
                        />
                      </div>
                    </div>
                    <div className="av-field">
                      <label htmlFor="av-cidade" className="av-label">Cidade / UF *</label>
                      <div className="av-input-wrap">
                        <i className="fa-solid fa-location-dot" aria-hidden="true" />
                        <input
                          id="av-cidade" name="cidade" type="text" required maxLength={100}
                          autoComplete="address-level2" placeholder="Ex: Palmas - TO"
                          value={dados.cidade} onChange={atualizar('cidade')} className="av-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="av-field">
                    <label htmlFor="av-segmento" className="av-label">Segmento da empresa *</label>
                    <div className="av-input-wrap">
                      <i className="fa-solid fa-store" aria-hidden="true" />
                      <select
                        id="av-segmento" name="segmento" required
                        value={dados.segmento} onChange={atualizar('segmento')}
                        className="av-input av-select"
                      >
                        <option value="">Selecione seu segmento</option>
                        {segmentos.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <i className="fa-solid fa-chevron-down av-select-seta" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="av-field">
                    <label htmlFor="av-mensagem" className="av-label">Mensagem</label>
                    <textarea
                      id="av-mensagem" name="mensagem" rows={4} maxLength={2000}
                      placeholder="Conte um pouco sobre sua empresa ou dúvidas sobre o sistema…"
                      value={dados.mensagem} onChange={atualizar('mensagem')}
                      className="av-input av-textarea"
                    />
                  </div>

                  <button type="submit" className="av-btn-submit" disabled={envio.tipo === 'enviando'}>
                    {envio.tipo === 'enviando' ? (
                      <><i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />Enviando…</>
                    ) : (
                      <><i className="fa-solid fa-paper-plane" aria-hidden="true" />Solicitar Demonstração</>
                    )}
                  </button>

                  {envio.tipo === 'erro' && (
                    <div className="av-alert-error" role="alert">
                      <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
                      <span>{envio.mensagem}</span>
                    </div>
                  )}
                </form>
              </>
            )}
          </div>

          {/* ── Lateral ── */}
          <aside className="av-lateral">
            <div className="av-side-card av-side-card--escuro">
              <h2 className="av-side-title">O que você vai ver na demonstração</h2>
              <ul className="av-checklist">
                {demonstracao.map(item => (
                  <li key={item}>
                    <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="av-side-card">
              <h2 className="av-side-title">Prefere falar agora?</h2>
              <div className="av-contatos">
                <a href="tel:+556332143357" className="av-contato">
                  <span className="av-contato-icon"><i className="fa-solid fa-phone" aria-hidden="true" /></span>
                  <span>
                    <span className="av-contato-label">Telefone</span>
                    <span className="av-contato-valor">(63)&nbsp;3214-3357</span>
                  </span>
                </a>
                <a href="https://wa.me/5563984042819" target="_blank" rel="noreferrer" className="av-contato">
                  <span className="av-contato-icon av-contato-icon--verde"><i className="fa-brands fa-whatsapp" aria-hidden="true" /></span>
                  <span>
                    <span className="av-contato-label">WhatsApp</span>
                    <span className="av-contato-valor">(63)&nbsp;98404-2819</span>
                  </span>
                </a>
                <a href="mailto:contato@sistemaquantum.com.br" className="av-contato">
                  <span className="av-contato-icon"><i className="fa-solid fa-envelope" aria-hidden="true" /></span>
                  <span>
                    <span className="av-contato-label">E-mail</span>
                    <span className="av-contato-valor">contato@sistemaquantum.com.br</span>
                  </span>
                </a>
              </div>
            </div>
          </aside>

        </div>
      </div>

    </div>
  )
}
